import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { AxiosError } from "axios";
import { TrashCan, } from "@unified-checkout/design-system/icons/index";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { SYSTEM_EVENTS_GROUPING_CODES } from "@app/dashboard/constants/tracking_events/system";
import { notify } from "@app/dashboard/utils/notifier";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { deleteUsers } from "@app/dashboard/services/inner_layout/user_management/users/delete-user";
import { deleteRole } from "@app/dashboard/services/inner_layout/user_management/roles/delete-role";
import type { IDeleteModal } from "@app/dashboard/types/innerLayout/user_management/modals/delete-modal";
import { ConfirmationModal } from "../ConfirmationModal";

import { getToasterConfig } from "../utils";


export const DeleteModal = ({
  type,
  open = false,
  setOpen,
  email,
  phoneNumber,
  userId,
  roleId,
  onSuccessHandler
}: IDeleteModal) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();


  const TEXT: Record<IDeleteModal['type'], Record<string, string>> = {
    removeMember: {
      modalTitle: t('innerLayout.userManagementPage.modals.removeMemberModal.supportingText').replace('{{email}}', email),
      modalSupportingText: '',
      primaryBtnText: t('innerLayout.userManagementPage.modals.removeMemberModal.primaryBtnText'),
      secondaryBtnText: t('form.cancel'),
      toasterText: t('innerLayout.userManagementPage.modals.removeMemberModal.toasterText'),
      toasterSupportingText: t('innerLayout.userManagementPage.modals.removeMemberModal.toasterSuppText').replace('{{email}}', email),
    },
    cancelInvitation: {
      modalTitle: t('innerLayout.userManagementPage.modals.cancelMemberInviteModal.title').replace('{{email}}', email || phoneNumber || ''),
      modalSupportingText: t('innerLayout.userManagementPage.modals.cancelMemberInviteModal.supportingText'),
      primaryBtnText: t('innerLayout.userManagementPage.modals.cancelMemberInviteModal.primaryBtnText'),
      secondaryBtnText: t('innerLayout.userManagementPage.modals.cancelMemberInviteModal.secondaryBtnText'),
      toasterText: t('innerLayout.userManagementPage.modals.cancelMemberInviteModal.toasterText'),
      toasterSupportingText: '',
    },
    deleteRole: {
      modalTitle: t('innerLayout.userManagementPage.modals.deleteRoleModal.title'),
      modalSupportingText: t('innerLayout.userManagementPage.modals.deleteRoleModal.supportingText'),
      primaryBtnText: t('innerLayout.userManagementPage.modals.deleteRoleModal.primaryBtnText'),
      secondaryBtnText: t('innerLayout.userManagementPage.modals.deleteRoleModal.secondaryBtnText'),
      toasterText: t('innerLayout.userManagementPage.modals.deleteRoleModal.toasterText'),
      toasterSupportingText: '',
    }
  };

  const mutationKey = type === 'removeMember' ?
    QUERY_KEYS.REMOVE_MEMBER
    :
    type === 'cancelInvitation' ?
      QUERY_KEYS.CANCEL_INVITATION
      :
      QUERY_KEYS.DELETE_ROLE;

  const mutationFn = type === 'removeMember' ?
    () => deleteUsers({ id: String(userId), type: 'removeMember' })
    :
    type === 'cancelInvitation' ?
      () => deleteUsers({ id: String(userId), type: 'cancelInvitation' })
      :
      () => deleteRole(roleId as string);

  const trackingCode = type === 'removeMember' ?
    SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.REMOVE_MEMBER
    :
    type === 'cancelInvitation' ?
      SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.CANCEL_INVITATION
      :
      SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.DELETE_ROLE;

  const {
    isPending,
    isError,
    data,
    mutate
  } = useMutation({
    mutationKey: [mutationKey],
    mutationFn: mutationFn,
    onSuccess: () => {
      setOpen?.(false);
      onSuccessHandler?.();

      type === 'deleteRole' ?
        queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_ROLES] })
        :
        queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_USERS] });

      notify.success(
        TEXT[type].toasterText,
        getToasterConfig({
          type: 'success',
          msg: TEXT[type].toasterText,
          trackingCode: trackingCode,
          extra: {
            hintText: TEXT[type].toasterText,
          },
        })
      );

      type === 'deleteRole' && navigate(INNER_ROUTES.MANAGE_ROLES);
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const msg = response?.data?.message || t('errors.somethingWentWrong');

      setOpen?.(false);
      notify.error(
        msg,
        getToasterConfig({
          type: 'error',
          msg,
          trackingCode: trackingCode
        })
      );
    },
  });

  const handleCancel = () => setOpen?.(false);

  const handleDelete = () => mutate();

  return (
    <>
      <ConfirmationModal
        modalProps={{
          state: "danger",
          open: open,
          headerIcon: type === 'removeMember' ? TrashCan : undefined,
          title: TEXT[type].modalTitle,
          supportingText: TEXT[type].modalSupportingText,
          btnsProps: [
            {
              size: "lg",
              color: "danger",
              variant: "contained",
              text: TEXT[type].primaryBtnText,
              onClickHandler: handleDelete,
              loading: isPending,
              disabled: isPending
            },
            {
              size: "lg",
              color: "secondary",
              variant: "outlined",
              text: TEXT[type].secondaryBtnText,
              onClickHandler: handleCancel
            }
          ]
        }}
      />      
    </>
  )
}
